import dayjs, { Dayjs } from "dayjs";
import { ExportOptionsFormState } from "src/features/observationsAdmin/components/ExportOptionsForm/ExportOptionsForm";

export const adaptFormType = (observationType: IObservationType) => {
	if (observationType === "INDIVIDUAL") {
		return "Individual Assessment";
	}

	if (observationType === "STORE_SCORED_ASSESSMENT") {
		return "Scored Store Assessment";
	}

	if (observationType === "GREAT_CONVERSATION") {
		return "Great Conversations";
	}

	if (observationType === "STORE_SELF_ASSESSMENT") {
		return "Non Scored Store Assessment";
	}

	return undefined;
};

const EXPORT_DATE_FORMAT = "YYYY-MM-DD HH:mm:ss";

export const calculateMonthlyDateRange = ({
	month,
	year
}: {
	month: string;
	year: number;
}) => {
	const months = Array.from({ length: 12 }, (_, index) =>
		dayjs().month(index).format("MMMM")
	);

	const monthIndex = months.indexOf(month);
	const dateInstance = dayjs().month(monthIndex).year(year);

	const startDate = dateInstance.startOf("month").format(EXPORT_DATE_FORMAT);
	const endDate = dateInstance.endOf("month").format(EXPORT_DATE_FORMAT);

	return { startDate, endDate };
};

export const calculateQuarterlyDateRange = ({
	quarter,
	year
}: {
	quarter: string;
	year: number;
}) => {
	let quarterNumber = 1;

	if (quarter === "Q1") quarterNumber = 1;
	if (quarter === "Q2") quarterNumber = 2;
	if (quarter === "Q3") quarterNumber = 3;
	if (quarter === "Q4") quarterNumber = 4;

	// Calculate the month index of the first month of the quarter (1 for Q1, 4 for Q2, 7 for Q3, 10 for Q4)
	const startMonthIndex = (quarterNumber - 1) * 3 + 1;

	const startDateString = `${year}-${startMonthIndex}-01`;

	const startDate = dayjs(startDateString)
		.startOf("month")
		.format(EXPORT_DATE_FORMAT);

	// Calculate the month index of the last month of the quarter (3 for Q1, 6 for Q2, 9 for Q3, 12 for Q4)
	const endMonthIndex = quarterNumber * 3;
	const endDateString = `${year}-${endMonthIndex}-01 23:59:59`;

	const endDate = dayjs(endDateString)
		.endOf("month")
		.format(EXPORT_DATE_FORMAT);

	return { startDate, endDate };
};

export const calculateYearlyDateRange = ({ year }: { year: number }) => {
	const startDate = dayjs(`${year}-01-01`)
		.startOf("year")
		.format(EXPORT_DATE_FORMAT);

	const endDate = dayjs(`${year}-12-31 23:59:59`)
		.endOf("day")
		.format(EXPORT_DATE_FORMAT);

	return { startDate, endDate };
};

export const calculateExportDateRange = ({
	exportType,
	month,
	quarter,
	year
}: ExportOptionsFormState) => {
	if (exportType === "Monthly" && month && year) {
		return calculateMonthlyDateRange({ month, year });
	}

	if (exportType === "Quarterly" && quarter && year) {
		return calculateQuarterlyDateRange({ quarter, year });
	}

	if (exportType === "Yearly" && year) {
		return calculateYearlyDateRange({ year });
	}

	return { startDate: undefined, endDate: undefined };
};

export const getCurrentQuarter = (currentDate: Dayjs) => {
	const month = currentDate.month() + 1;

	if (month >= 1 && month <= 3) {
		return "Q1";
	}

	if (month >= 4 && month <= 6) {
		return "Q2";
	}

	if (month >= 7 && month <= 9) {
		return "Q3";
	}

	return "Q4";
};

export const answerExists = ({
	answers,
	answerGiven,
	answerId
}: {
	answerGiven: string;
	answers?: any[];
	answerId: number | null;
}) => {
	const normalizeString = (str: string) => str?.toLowerCase().trim();
	const normalizedAnswerGiven = normalizeString(answerGiven);

	const existingAnswer = answers?.find((answer: any) => {
		const normalizedAnswerName = normalizeString(answer.name);

		return (
			normalizedAnswerName === normalizedAnswerGiven && answer.id !== answerId
		);
	});

	return Boolean(existingAnswer);
};
